<template>
  <div class="consumer-details-container">
    <!-- 页面头部：标题 -->
    <div class="page-header">
      <h2>消费者详情</h2>
    </div>

    <!-- 1. 消费者基本信息卡片 -->
    <el-card shadow="hover" class="base-info-card" margin-bottom="20px">
      <div class="card-header">
        <span class="card-title">基本信息</span>
        <!-- 启用/禁用按钮 -->
        <el-button
          :type="consumerInfo.consumerStatus === '1' ? 'danger' : 'success'"
          size="mini"
          @click="openStatusDialog"
        >
          {{ consumerInfo.consumerStatus === "1" ? "禁用" : "启用" }}
        </el-button>
      </div>
      <el-descriptions
        :column="2"
        border
        class="base-info-des"
        label-width="120px"
      >
        <el-descriptions-item label="消费者OpenID">
          <span class="text-overflow">{{
            consumerInfo.openid || "未获取"
          }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="注册手机号">
          {{ formatPhone(consumerInfo.registeredMobile) || "未设置" }}
        </el-descriptions-item>
        <el-descriptions-item label="微信名字">
          {{ consumerInfo.wechatName || "未知用户" }}
        </el-descriptions-item>
        <el-descriptions-item label="注册时间">
          {{ formatDateTime(consumerInfo.registrationTime) || "无" }}
        </el-descriptions-item>
        <el-descriptions-item label="当前状态">
          <el-tag
            :type="consumerInfo.consumerStatus === '1' ? 'success' : 'danger'"
            size="medium"
          >
            {{ consumerInfo.consumerStatus === "1" ? "启用" : "禁用" }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="订单总数">
          {{ consumerInfo.orderCount || 0 }}
        </el-descriptions-item>
      </el-descriptions>
      <!-- 备注区域 -->
      <div class="remark-section">
        <span class="remark-label">商家备注：</span>
        <div class="remark-content-wrap">
          <span class="remark-content">
            {{ consumerInfo.consumerNotes || "暂无备注信息" }}
          </span>
          <el-button
            type="text"
            class="edit-remark-btn"
            @click="openRemarkDialog"
          >
            编辑备注
          </el-button>
        </div>
      </div>
    </el-card>

    <!-- 2. 收货地址列表卡片（核心修改：新增显示/隐藏功能） -->
    <el-card shadow="hover" class="address-card" margin-bottom="20px">
      <div slot="header" class="card-header">
        <span class="card-title">收货地址（共{{ addressList.length }}个）</span>
        <!-- 显示/隐藏切换按钮 -->
        <el-button
          type="text"
          class="address-toggle-btn"
          @click="toggleAddressMask"
        >
          {{ addressMaskVisible ? "显示" : "隐藏" }}
        </el-button>
      </div>
      <el-table
        :data="addressList"
        border
        stripe
        style="width: 100%"
        empty-text="该消费者暂无收货地址"
      >
        <!-- 收货人列：首字符真实，其余* -->
        <el-table-column label="收货人" width="140" align="center">
          <template #default="{ row }">
            {{ formatRecipientName(row.recipientName, addressMaskVisible) }}
            {{ row.recipientGender }}
          </template>
        </el-table-column>
        <!-- 手机号列：中间四位****（复用原有格式化） -->
        <el-table-column label="手机号" width="160" align="center">
          <template #default="{ row }">
            {{
              addressMaskVisible
                ? formatPhone(row.mobileNumber)
                : row.mobileNumber || "未填写"
            }}
          </template>
        </el-table-column>
        <!-- 收货地址列：前10位真实，其余* -->
        <el-table-column label="收货地址" min-width="350">
          <template #default="{ row }">
            <div class="address-text">
              {{ formatAddress(row.deliveryAddress, addressMaskVisible) }}
            </div>
          </template>
        </el-table-column>
        <!-- 标签列：脱敏时显示* -->
        <el-table-column label="标签" width="120" align="center">
          <template #default="{ row }">
            <el-tag size="mini" :type="getLabelTagType(row.label)">
              {{ formatLabel(row.label, addressMaskVisible) }}
            </el-tag>
          </template>
        </el-table-column>
        <!-- 默认地址列（无修改） -->
        <el-table-column label="默认地址" width="120" align="center">
          <template #default="{ row }">
            <span>{{ row.isDefault ? "是" : "否" }}</span>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 3. 订单列表卡片（无修改） -->
    <el-card shadow="hover" class="order-card">
      <div slot="header" class="card-header">
        <span class="card-title"
          >订单信息（订单数 {{ consumerInfo.orderCount || 0 }}）</span
        >
      </div>
      <el-table
        :data="orderList"
        border
        stripe
        style="width: 100%"
        v-loading="orderLoading"
        element-loading-text="加载订单数据中..."
        empty-text="该消费者暂无订单记录"
      >
        <el-table-column label="订单号码" width="220">
          <template #default="{ row }">
            <span class="order-id">{{ row._id || "未知订单" }}</span>
          </template>
        </el-table-column>
        <!-- 商品名称列：显示商品名+单价+数量 + 总件数 -->
        <el-table-column label="商品名称" min-width="350">
          <template #default="{ row }">
            <div class="goods-name-wrap">
              <!-- 遍历每个商品项，独立绑定鼠标事件 -->
              <div
                class="goods-item"
                v-for="(goods, index) in row.goodsList"
                :key="index"
                @mouseenter="showGoodsId(goods.goodsId)"
                @mouseleave="hideGoodsId"
              >
                <!-- 商品名称 -->
                <span class="goods-name-text">{{
                  goods.goodsName || "未知商品"
                }}</span>
                <!-- 商品单价 -->
                <span class="goods-price"
                  >¥{{ (goods.goodsPrice || 0).toFixed(2) }}</span
                >
                <!-- 商品数量 -->
                <span class="goods-quantity"
                  >×{{ goods.singleGoodQuantity || 0 }}</span
                >
                <!-- 商品ID悬浮提示：仅当前商品匹配时显示 -->
                <el-tooltip
                  v-model="goodsIdVisible"
                  :content="`商品ID：${currentGoodsId}`"
                  placement="top"
                  effect="dark"
                  popper-class="goods-id-tooltip"
                  :disabled="currentGoodsId !== goods.goodsId"
                >
                  <span class="tooltip-trigger"></span>
                </el-tooltip>
              </div>
              <!-- 订单总件数（取自订单的goodsTotalQuantity） -->
              <div class="goods-total-count">
                共{{ row.goodsTotalQuantity || 0 }}件
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="订单总计" width="120" align="center">
          <template #default="{ row }">
            <span class="total-amount"
              >¥{{ (row.totalAmount || 0).toFixed(2) }}</span
            >
          </template>
        </el-table-column>
        <el-table-column label="下单时间" width="200" align="center">
          <template #default="{ row }">
            {{ formatDateTime(row.orderTime) || "无" }}
          </template>
        </el-table-column>
        <el-table-column label="快递单号" width="180" align="center">
          <template #default="{ row }">
            <span class="express-no">{{ row.expressNumber || "未填写" }}</span>
          </template>
        </el-table-column>
        <el-table-column label="订单备注" width="150">
          <template #default="{ row }">
            <span class="order-remark">{{ row.orderNotes || "无" }}</span>
          </template>
        </el-table-column>
        <el-table-column label="订单状态" width="120" align="center">
          <template #default="{ row }">
            <el-tag :type="getOrderStatusTagType(row.orderStatus)" size="mini">
              {{ row.orderStatus || "未知状态" }}
            </el-tag>
          </template>
        </el-table-column>
      </el-table>
      <!-- 订单分页器 -->
      <div class="order-pagination">
        <el-pagination
          background
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="orderPagination.currentPage"
          :page-sizes="[5, 10, 20]"
          :page-size="orderPagination.pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="orderPagination.total"
        ></el-pagination>
      </div>
    </el-card>

    <!-- 4. 备注编辑弹窗（无修改） -->
    <el-dialog
      title="编辑消费者备注"
      :visible.sync="remarkDialogVisible"
      width="500px"
      center
      :before-close="closeRemarkDialog"
    >
      <el-form :model="remarkForm" label-width="100px">
        <el-form-item label="备注内容">
          <el-input
            type="textarea"
            rows="4"
            v-model="remarkForm.content"
            placeholder="请输入备注信息（例如：VIP客户、风控关注等）"
            maxlength="500"
            show-word-limit
            clearable
          ></el-input>
          <div class="remark-tip">备注仅商家可见，最多支持500个字符</div>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="remarkDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitRemark">保存备注</el-button>
      </div>
    </el-dialog>

    <!-- 5. 启用/禁用确认弹窗（无修改） -->
    <el-dialog
      :title="consumerInfo.consumerStatus === '1' ? '禁用消费者' : '启用消费者'"
      :visible.sync="statusDialogVisible"
      width="400px"
      center
    >
      <div class="status-dialog-content">
        <p>
          确认{{
            consumerInfo.consumerStatus === "1" ? "禁用" : "启用"
          }}该消费者吗？
        </p>
        <p v-if="consumerInfo.consumerStatus === '1'" class="warning-text">
          注：禁用后消费者将无法下单，需手动启用才能恢复
        </p>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="statusDialogVisible = false">取消</el-button>
        <el-button
          :type="consumerInfo.consumerStatus === '1' ? 'danger' : 'success'"
          @click="submitStatusChange"
        >
          确认{{ consumerInfo.consumerStatus === "1" ? "禁用" : "启用" }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: "ConsumerDetails",
  data() {
    return {
      // 核心数据
      consumerId: "", // 当前消费者ID（从路由参数获取）
      consumerInfo: {}, // 消费者基本信息
      addressList: [], // 收货地址列表
      orderList: [], // 订单列表
      orderLoading: false, // 订单加载状态
      // 新增：地址显示/隐藏状态（true=脱敏显示，false=完整显示）
      addressMaskVisible: true,
      // 弹窗与交互状态
      remarkDialogVisible: false, // 备注编辑弹窗
      statusDialogVisible: false, // 启用/禁用弹窗
      goodsIdVisible: false, // 商品ID提示显隐
      currentGoodsId: "", // 当前显示的商品ID
      // 表单数据
      remarkForm: {
        content: "", // 备注内容
      },
      // 分页配置（订单列表）
      orderPagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0,
      },
    };
  },
  mounted() {
    // 1. 从路由参数获取消费者ID
    this.consumerId = this.$route.params.consumerId;
    if (!this.consumerId) {
      this.$message.error("消费者ID缺失，无法加载详情");
      this.$router.go(-1);
      return;
    }
    // 2. 加载消费者详情数据（基本信息+地址+订单）
    this.fetchConsumerDetail();
  },
  methods: {
    /**
     * 1. 获取消费者基本信息 + 收货地址
     */
    async fetchConsumerDetail() {
      try {
        // 校验Token
        const token = localStorage.getItem("token");
        if (!token) {
          this.$message.warning("请先登录");
          this.$router.push("/MerchantLogin");
          return;
        }
        // 调用详情接口
        const res = await this.$http.get(
          `/api/merchant/consumers/${this.consumerId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (res.data.code !== 0) {
          throw new Error(res.data.message || "获取消费者信息失败");
        }
        // 赋值基本信息和地址列表
        const { consumer, receiptInfos } = res.data.data;
        this.consumerInfo = consumer;
        this.addressList = receiptInfos || [];
        // 初始化备注表单
        this.remarkForm.content = consumer.consumerNotes || "";
      } catch (error) {
        console.error("加载消费者详情失败：", error);
        // 分类错误处理
        if (error.response?.status === 401) {
          this.$message.error("登录已过期，请重新登录");
          localStorage.removeItem("token");
          this.$router.push("/MerchantLogin");
        } else if (error.response?.status === 404) {
          this.$message.error("消费者不存在或已删除");
          this.$router.go(-1);
        } else {
          this.$message.error(error.message || "加载失败，请稍后重试");
        }
      } finally {
        // 无论基本信息是否加载成功，都尝试加载订单
        this.fetchConsumerOrders();
      }
    },

    /**
     * 2. 获取消费者订单列表（带分页）
     */
    async fetchConsumerOrders() {
      try {
        this.orderLoading = true;
        const token = localStorage.getItem("token");
        if (!token) {
          this.$message.warning("请先登录");
          this.$router.push("/MerchantLogin");
          return;
        }
        // 构建请求参数（分页+排序）
        const params = {
          page: this.orderPagination.currentPage,
          pageSize: this.orderPagination.pageSize,
          sortBy: "orderTime", // 按订单时间排序
          sortOrder: "desc", // 倒序（最新订单在前）
        };
        // 调用订单列表接口
        const res = await this.$http.get(
          `/api/merchant/consumers/${this.consumerId}/orders`,
          {
            params,
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        // 校验接口返回状态
        if (res.data.code !== 0) {
          throw new Error(res.data.message || "获取订单列表失败");
        }
        // 赋值订单数据和分页信息（包含goodsPrice和goodsTotalQuantity）
        const { orders, pagination } = res.data.data;
        this.orderList = orders || [];
        this.orderPagination = {
          currentPage: pagination.currentPage || 1,
          pageSize: pagination.pageSize || 10,
          total: pagination.total || 0,
        };
        // 空数据提示
        if (this.orderList.length === 0 && this.orderPagination.total === 0) {
          this.$message.info("该消费者暂无订单记录");
        }
      } catch (error) {
        console.error("加载消费者订单失败：", error);
        // 分类错误处理
        if (error.response?.status === 401) {
          this.$message.error("登录已过期，请重新登录");
          localStorage.removeItem("token");
          this.$router.push("/MerchantLogin");
        } else if (error.response?.status === 404) {
          this.$message.error("消费者不存在，无法获取订单");
        } else {
          this.$message.error(error.message || "订单加载失败，请稍后重试");
        }
        // 错误时重置数据
        this.orderList = [];
        this.orderPagination.total = 0;
      } finally {
        this.orderLoading = false; // 关闭加载状态
      }
    },

    /**
     * 3. 提交备注编辑
     */
    async submitRemark() {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          this.$message.warning("请先登录");
          this.$router.push("/MerchantLogin");
          return;
        }
        // 内容去重空格
        const trimmedContent = this.remarkForm.content.trim();
        // 调用备注更新接口
        const res = await this.$http.put(
          `/api/merchant/consumers/${this.consumerId}/remark`,
          { consumerNotes: trimmedContent },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.data.code === 0) {
          this.$message.success("备注保存成功");
          this.remarkDialogVisible = false;
          // 更新本地备注信息
          this.consumerInfo.consumerNotes = trimmedContent;
        } else {
          throw new Error(res.data.message || "备注保存失败");
        }
      } catch (error) {
        console.error("提交备注失败：", error);
        this.$message.error(error.message || "保存备注失败，请稍后重试");
      }
    },

    /**
     * 4. 提交启用/禁用状态变更
     */
    async submitStatusChange() {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          this.$message.warning("请先登录");
          this.$router.push("/MerchantLogin");
          return;
        }
        // 目标状态：1=启用，-1=禁用
        const targetStatus =
          this.consumerInfo.consumerStatus === "1" ? "-1" : "1";
        // 调用状态更新接口
        const res = await this.$http.put(
          `/api/merchant/consumers/${this.consumerId}/status`,
          { consumerStatus: targetStatus },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.data.code === 0) {
          const statusText = targetStatus === "1" ? "启用" : "禁用";
          this.$message.success(`消费者${statusText}成功`);
          this.statusDialogVisible = false;
          // 更新本地状态
          this.consumerInfo.consumerStatus = targetStatus;
        } else {
          throw new Error(res.data.message || "状态更新失败");
        }
      } catch (error) {
        console.error("变更消费者状态失败：", error);
        this.$message.error(error.message || "状态更新失败，请稍后重试");
      }
    },

    /**
     * 5. 显示当前商品的ID悬浮提示
     */
    showGoodsId(goodsId) {
      if (goodsId) {
        this.currentGoodsId = goodsId;
        this.goodsIdVisible = true;
      }
    },

    /**
     * 6. 隐藏商品ID悬浮提示
     */
    hideGoodsId() {
      this.goodsIdVisible = false;
      this.currentGoodsId = "";
    },

    /**
     * 7. 获取地址标签的Tag类型
     */
    getLabelTagType(label) {
      const tagTypeMap = {
        家: "success",
        公司: "primary",
        学校: "warning",
        父母家: "info",
      };
      return tagTypeMap[label] || "default";
    },

    /**
     * 8. 获取订单状态的Tag类型
     */
    getOrderStatusTagType(status) {
      const tagTypeMap = {
        待发货: "warning",
        待收货: "primary",
        完成: "success",
        已取消: "danger",
      };
      return tagTypeMap[status] || "info";
    },

    /**
     * 9. 分页：每页条数变更
     */
    handleSizeChange(size) {
      this.orderPagination.pageSize = size;
      this.orderPagination.currentPage = 1;
      this.fetchConsumerOrders();
    },

    /**
     * 10. 分页：当前页码变更
     */
    handleCurrentChange(page) {
      this.orderPagination.currentPage = page;
      this.fetchConsumerOrders();
    },

    /**
     * 11. 打开备注弹窗
     */
    openRemarkDialog() {
      this.remarkForm.content = this.consumerInfo.consumerNotes || "";
      this.remarkDialogVisible = true;
    },

    /**
     * 12. 关闭备注弹窗（清空表单）
     */
    closeRemarkDialog() {
      this.remarkDialogVisible = false;
      this.remarkForm.content = "";
    },

    /**
     * 13. 打开启用/禁用弹窗
     */
    openStatusDialog() {
      this.statusDialogVisible = true;
    },

    /**
     * 14. 工具方法：格式化手机号（中间四位打码）
     */
    formatPhone(phone) {
      if (!phone || phone.length !== 11) return phone;
      return phone.slice(0, 3) + "****" + phone.slice(7);
    },

    /**
     * 15. 工具方法：格式化时间（ISO字符串转北京时间）
     */
    formatDateTime(isoString) {
      if (!isoString) return "无";
      const date = new Date(isoString);
      // 转北京时间（+8小时）
      const beijingTime = new Date(date.getTime() + 8 * 60 * 60 * 1000);
      return beijingTime.toISOString().replace("T", " ").slice(0, 19);
    },

    /**
     * 16. 新增：切换地址显示状态（脱敏 ↔ 完整）
     */
    toggleAddressMask() {
      this.addressMaskVisible = !this.addressMaskVisible;
    },

    /**
     * 17. 新增：格式化收货人姓名（首字符真实，其余*）
     */
    formatRecipientName(name, maskStatus) {
      if (!name) return "未填写";
      if (!maskStatus) return name;
      if (name.length === 1) return name;
      return name.charAt(0) + "*".repeat(name.length - 1);
    },

    /**
     * 18. 新增：格式化收货地址（前10位真实，其余*）
     */
    formatAddress(addressObj, maskStatus) {
      if (!addressObj) return "未填写";
      // 拼接完整地址（address+名称+门牌号）
      const fullAddress = `${addressObj.address || ""}${addressObj.name || ""}${
        addressObj.houseNumber || ""
      }`;
      if (!maskStatus) return fullAddress;
      const maxShowLength = 10;
      if (fullAddress.length <= maxShowLength) return fullAddress;
      return (
        fullAddress.slice(0, maxShowLength) +
        "*".repeat(fullAddress.length - maxShowLength)
      );
    },

    /**
     * 19. 新增：格式化标签（脱敏时显示*）
     */
    formatLabel(label, maskStatus) {
      if (maskStatus) return "*";
      return label || "无标签";
    },
  },
};
</script>

<style scoped>
/* 容器基础样式 */
.consumer-details-container {
  padding: 20px;
  background-color: #fff;
  min-height: calc(100vh - 64px);
}

/* 页面头部 */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

/* 卡片通用样式 */
.base-info-card,
.address-card,
.order-card {
  margin-bottom: 20px;
  border-radius: 4px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.card-title {
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

/* 新增：地址切换按钮样式（与编辑备注按钮风格统一） */
.address-toggle-btn {
  color: #409eff;
  padding: 0;
  font-size: 12px;
}

/* 基本信息描述列表 */
.base-info-des {
  margin-bottom: 20px;
}

.text-overflow {
  display: inline-block;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 备注区域 */
.remark-section {
  display: flex;
  align-items: flex-start;
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.remark-label {
  font-weight: 500;
  color: #666;
  margin-right: 12px;
  white-space: nowrap;
}

.remark-content-wrap {
  flex: 1;
}

.remark-content {
  display: block;
  color: #333;
  line-height: 1.5;
  word-break: break-all;
  margin-bottom: 8px;
}

.edit-remark-btn {
  color: #409eff;
  padding: 0;
  font-size: 12px;
}

/* 收货地址表格 */
.address-text {
  line-height: 1.5;
  color: #333;
}

/* 订单表格 - 商品名称列样式 */
.goods-name-wrap {
  padding: 4px 0;
}

/* 单个商品项样式（相对定位，用于Tooltip） */
.goods-item {
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: 4px;
  line-height: 1.4;
}

/* 商品名称文本 */
.goods-name-text {
  flex: 1;
  min-width: 120px;
  color: #333;
}

/* 商品单价 */
.goods-price {
  margin: 0 8px;
  color: #f56c6c;
  white-space: nowrap;
}

/* 商品数量 */
.goods-quantity {
  color: #666;
  white-space: nowrap;
}

/* 订单总件数 */
.goods-total-count {
  margin-top: 4px;
  font-weight: 500;
  color: #666;
  font-size: 13px;
}

/* 商品ID提示触发器（覆盖当前商品项） */
.tooltip-trigger {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

/* 商品ID提示框样式 */
.goods-id-tooltip {
  max-width: 200px;
  font-size: 12px;
}

/* 订单表格其他样式 */
.order-id,
.express-no,
.order-remark {
  font-size: 12px;
  color: #666;
}

.total-amount {
  color: #606266;
  font-weight: 500;
}

/* 订单分页器 */
.order-pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

/* 弹窗样式 */
.remark-tip {
  margin-top: 8px;
  font-size: 12px;
  color: #666;
}

.status-dialog-content {
  margin-bottom: 8px;
}

.warning-text {
  color: #f56c6c;
  font-size: 12px;
  margin-top: 8px;
}

.dialog-footer {
  text-align: right;
}
</style>